import React, { Component } from "react";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import themeLarge from "../component/ThemeLarge";
import Footer from "../component/Footer";

/**
 * Components
 */
import HeaderRow from "../component/dash/HeaderRow";
import PublishedBlogsList from "../component/dash/PublishedBlogsList";
import DraftBlogsList from "../component/dash/DraftBlogsList";
import DeleteModal from "../component/dash/DeleteModal";
import DashboardSidebar from "../component/dash/DashboardSidebar";

class Dashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      userEmail: null,
      currentTime: new Date().toLocaleTimeString(),
      draftBlogs: [], // Store fetched draft blogs
      publishedBlogs: [], // Store fetched published blogs
      blogToDelete: null, // Store the blog ID for the delete action
      featuredId: null, // Store which blogID is featured
      isSidebarOpen: false, // checks if the sidebar is open
    };
  }

  componentDidMount() {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      this.setState({ userEmail: user.email });
    }

    // Update time every second
    this.intervalID = setInterval(() => {
      this.setState({ currentTime: new Date().toLocaleTimeString() });
    }, 1000);

    // Load draft and published blogs
    this.loadDraftBlogs();
    this.loadPublishedBlogs();
    this.loadFeaturedBlog();
  }

  componentWillUnmount() {
    clearInterval(this.intervalID);
  }

  handleCreateNewBlog = () => {
    this.props.navigate("/dashboard/create-new");
  };

  loadDraftBlogs = async () => {
    // 1. Load local-only drafts (no Firestore ID)
    const localDrafts = Object.keys(localStorage)
      .filter((key) => key.startsWith("cachedDraft_"))
      .map((key) => {
        try {
          const draft = JSON.parse(localStorage.getItem(key));
          return {
            id: null, // No Firestore ID
            ...draft,
            isLocalOnly: true,
            localCacheKey: key,
          };
        } catch {
          return null;
        }
      })
      .filter((draft) => draft !== null);

    this.setState({
      draftBlogs: [...localDrafts],
    });
  };

  // Fetch published blogs from Firestore with 1-day cache
  loadPublishedBlogs = async () => {
    const CACHE_KEY = "publishedBlogsCache";
    const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 1 day in milliseconds

    const cachedData = localStorage.getItem(CACHE_KEY);

    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        const now = new Date().getTime();

        if (now - parsed.timestamp < CACHE_EXPIRY_MS) {
          this.setState({ publishedBlogs: parsed.data });
          return;
        }
      } catch (err) {
        console.warn("Failed to parse cache, fetching fresh data", err);
      }
    }

    // No valid cache, fetch from Firestore
    try {
      const db = getFirestore();
      const q = query(
        collection(db, "blogs"),
        where("status", "==", "published")
      );
      const querySnapshot = await getDocs(q);
      const fetchedBlogs = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.(), // Convert Timestamp to Date
        };
      });

      // Get featured blog ID from state
      const { featuredId } = this.state;

      // Sort: featured first, then by createdAt descending
      const sortedBlogs = fetchedBlogs.sort((a, b) => {
        // Featured blog always first
        if (a.id === featuredId) return -1;
        if (b.id === featuredId) return 1;
        return b.createdAt - a.createdAt;
      });

      // Save to state and cache
      this.setState({ publishedBlogs: sortedBlogs });
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: sortedBlogs,
          timestamp: new Date().getTime(),
        })
      );
      console.log("Fetched published blogs and saved to cache");
    } catch (error) {
      console.error("Error fetching published blogs:", error);
    }
  };

  // Load Featured Blog
  loadFeaturedBlog = async () => {
    const CACHE_KEY = "featuredBlogCache";
    const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 1 day

    // Try loading from cache first
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        const now = Date.now();

        if (now - parsed.timestamp < CACHE_EXPIRY_MS) {
          this.setState({ featuredId: parsed.blogId });
          return;
        }
      } catch (e) {
        console.warn("⚠️ Failed to parse featured cache");
      }
    }

    // Fallback: Load from Firestore
    try {
      const featuredRef = doc(getFirestore(), "featured", "current");
      const snap = await getDoc(featuredRef);

      if (snap.exists()) {
        const blogId = snap.data().blogId;

        // Cache it
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            blogId,
            timestamp: Date.now() + CACHE_EXPIRY_MS,
          })
        );

        // Update state
        this.setState({ featuredId: blogId });
      }
    } catch (err) {
      console.warn("❌ Couldn't fetch featured blog ID:", err);
    }
  };

  // Open modal to confirm delete
  openDeleteModal = (blogId) => {
    this.setState({ blogToDelete: blogId });
    const modal = new window.bootstrap.Modal(
      document.getElementById("deleteModal")
    );
    modal.show();
  };

  // Handle confirmed delete action
  handleDeleteBlog = async () => {
    const { blogToDelete, publishedBlogs } = this.state;
    const db = getFirestore();
    const blogRef = doc(db, "blogs", blogToDelete);

    try {
      // 1. Soft delete in Firestore
      await updateDoc(blogRef, {
        status: "deleted",
      });

      // 2. Remove from state
      const updatedPublished = publishedBlogs.filter(
        (blog) => blog.id !== blogToDelete
      );

      this.setState({
        publishedBlogs: updatedPublished,
        blogToDelete: null,
      });

      // 3. Replace from localStorage cache
      localStorage.setItem(
        "publishedBlogsCache",
        JSON.stringify({
          data: updatedPublished,
          timestamp: Date.now(),
        })
      );

      // 4. Hide modal
      const modal = window.bootstrap.Modal.getInstance(
        document.getElementById("deleteModal")
      );
      modal.hide();

      const backdrop = document.querySelector(".modal-backdrop");
      if (backdrop) {
        backdrop.remove();
      }
    } catch (error) {
      console.error("Error soft-deleting blog: ", error);
    }
  };

  handleEditBlog = (blogId) => {
    if (blogId.startsWith("cachedDraft_")) {
      this.props.navigate(`/dashboard/create-new?id=${blogId}`);
    } else {
      this.props.navigate(`/dashboard/create-new?id=${blogId}`);
    }
  };

  handleDeleteDraftBlog = (draftId) => {
    // Remove from localStorage
    localStorage.removeItem(draftId);

    // Update state
    const updatedDrafts = this.state.draftBlogs.filter((blog) => {
      const cacheKey = blog.localCacheKey || blog.cacheKey;
      return cacheKey !== draftId;
    });

    alert("Draft blog removed from Local cache! It's gone forever!");

    this.setState({
      draftBlogs: updatedDrafts,
    });
  };

  handleToggleFeatured = async (blogId) => {
    const CACHE_KEY = "featuredBlogCache";
    const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 1 day

    const { publishedBlogs } = this.state;
    const db = getFirestore();
    const featuredRef = doc(db, "featured", "current");

    try {
      const snap = await getDoc(featuredRef);
      const currentFeaturedId = snap.exists() ? snap.data().blogId : null;

      const newFeaturedId = currentFeaturedId === blogId ? null : blogId;

      // Set or clear featured blog
      await setDoc(featuredRef, { blogId: newFeaturedId });

      // 1. Find the blog title for notification
      const toggledBlog = publishedBlogs.find((blog) => blog.id === blogId);
      const blogTitle = toggledBlog?.title || "Unknown Blog";

      // 2. Show alert or custom toast
      if (newFeaturedId) {
        alert(`✅ "${blogTitle}" is now featured.`);
      } else {
        alert(`🗑️ "${blogTitle}" is no longer featured.`);
      }

      // 3. Close the dropdown manually (Bootstrap)
      const dropdownElement = document.querySelector(
        `[data-bs-toggle="dropdown"][id="dropdownMenuButton-${blogId}"]`
      );
      if (dropdownElement) {
        const dropdownInstance =
          window.bootstrap.Dropdown.getInstance(dropdownElement);
        if (dropdownInstance) dropdownInstance.hide();
      }

      // 4. Update local state to reflect featured blog immediately
      this.setState({ featuredId: newFeaturedId });
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          blogId,
          timestamp: Date.now() + CACHE_EXPIRY_MS,
        })
      );
    } catch (err) {
      alert("❌ Failed to toggle featured blog.");
    }
  };

  toggleSidebar = () => {
    this.setState((prev) => ({ isSidebarOpen: !prev.isSidebarOpen }));
  };

  render() {
    const { userEmail, currentTime, draftBlogs, publishedBlogs } = this.state;

    return (
      <div
        style={{
          display: "flex",
          height: "calc(100vh - 56px)",
          marginTop: "85px",
        }}
      >
        <DashboardSidebar
          isOpen={this.state.isSidebarOpen}
          onToggle={this.toggleSidebar}
        />

        {/* main dashboard content */}

        <div
          className="container-fluid"
          style={{
            background: themeLarge.colors.bg,
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "15px",
            marginTop: "-10px",
            transition: "margin-left 0.3s ease-in",
            flexGrow: 1,
          }}
        >
          <HeaderRow
            userEmail={userEmail}
            currentTime={currentTime}
            onCreateBlog={this.handleCreateNewBlog}
          />

          {/* Main content: left 40%, right 60% */}
          <div
            className="row"
            style={{
              backgroundColor: themeLarge.colors.secondary,
              height: "calc(80vh - 80px)",
              overflow: "hidden",
            }}
          >
            {/* Left column (40% width) - Published Blogs */}
            <div
              className="col-6"
              style={{
                height: "100%",
                overflowY: "auto",
                paddingRight: "10px",
              }}
            >
              <PublishedBlogsList
                publishedBlogs={publishedBlogs}
                featuredId={this.state.featuredId}
                onEdit={this.handleEditBlog}
                onDelete={this.openDeleteModal}
                onToggleFeatured={this.handleToggleFeatured}
              />
            </div>

            {/* Right column (60% width) */}
            <div
              className="col-6"
              style={{
                height: "100%",
                overflowY: "auto",
                paddingLeft: "10px",
              }}
            >
              <DraftBlogsList
                draftBlogs={draftBlogs}
                onEdit={this.handleEditBlog}
                onDelete={this.handleDeleteDraftBlog}
              />
            </div>
          </div>
          <Footer />

          {/* Delete Confirmation Modal */}
          <DeleteModal
            blogToDelete={this.state.blogToDelete}
            onConfirmDelete={this.handleDeleteBlog}
          />
        </div>
      </div>
    );
  }
}

// withNavigate HOC to access navigation (useNavigate alternative for class-based components)
function withNavigate(Component) {
  return function Wrapper(props) {
    const navigate = useNavigate();
    return <Component {...props} navigate={navigate} />;
  };
}

export default withNavigate(Dashboard);
